import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/common/Logo";
import { useAuth } from "@/contexts/AuthContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const { user, profile } = useAuth();

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <nav className="fixed top-4 left-4 right-4 z-50 glass rounded-2xl shadow-lg">
      <div className="px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-8 text-sm">
            <Link to="/" className="relative font-medium text-foreground hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left pb-0.5">
              Home
            </Link>
            <Link to="/courses" className="relative font-medium text-foreground hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left pb-0.5">
              Courses
            </Link>
            <a href="/#features" className="relative font-medium text-foreground hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left pb-0.5">
              Features
            </a>
            <Link to="/our-story" className="relative font-medium text-foreground hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left pb-0.5">
              Our Story
            </Link>
            <Link to="/how-it-works" className="relative font-medium text-foreground hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left pb-0.5">
              How It Works
            </Link>
            <Link to="/earn-money" className="relative font-medium text-foreground hover:text-primary transition-colors after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left pb-0.5">
              Earn Money
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="flex items-center gap-3">
                <Button variant="gold">
                  Dashboard
                </Button>
                <Avatar className="w-9 h-9 border-2 border-border">
                  <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name} />
                  <AvatarFallback className="bg-muted text-foreground text-sm font-semibold">
                    {profile ? getInitials(profile.full_name) : "U"}
                  </AvatarFallback>
                </Avatar>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline">
                    Log In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="gold">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="lg:hidden p-2 text-foreground"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-6 border-t border-border/40 animate-fade-up">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Home
              </Link>
              <Link to="/courses" className="text-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Courses
              </Link>
              <a href="/#features" className="text-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Features
              </a>
              <Link to="/our-story" className="text-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Our Story
              </Link>
              <Link to="/how-it-works" className="text-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                How It Works
              </Link>
              <Link to="/earn-money" className="text-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Earn Money
              </Link>
              <div className="flex flex-col gap-3 pt-4 border-t border-border/40">
                {user ? (
                  <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                    <Button variant="gold" className="w-full">Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full">Log In</Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsOpen(false)}>
                      <Button variant="gold" className="w-full">Get Started</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
